import React from "react";

/* ═══════════════════════════════════════════════════════════════
   Human Body — 8 templates
   brain_diagram, eye_anatomy, ear_anatomy, skeleton_system,
   digestive_tract, muscle_fiber, tooth_cross_section, skin_layers
   ═══════════════════════════════════════════════════════════════ */

const bodyGrad = (id: string, c1: string, c2: string) => (
  <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
    <stop offset="0%" stopColor={c1} /><stop offset="100%" stopColor={c2} />
  </linearGradient>
);

/* ─── 1. Brain Diagram ────────────────────────────────────── */
export const BrainDiagram: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Brain";
  const highlightTarget = (props.highlightTarget as string) || "";
  const accentColor = (props.accentColor as string) || "#e91e63";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {bodyGrad("br-bg", "#1a1a2e", "#0d0d1a")}
        <filter id="br-glow"><feDropShadow dx="0" dy="2" stdDeviation="6" floodColor={accentColor} floodOpacity="0.3" /></filter>
      </defs>
      <rect width="800" height="600" fill="url(#br-bg)" rx="16" />
      <g filter="url(#br-glow)" transform="translate(140,40)">
        {/* Left hemisphere */}
        <path d="M260,120 Q180,80 140,160 Q100,240 120,320 Q140,400 220,420 L260,400Z"
          fill={highlightTarget === "left" ? accentColor : "#e57373"} opacity={highlightTarget === "left" ? 1 : 0.7}
          stroke="#ef9a9a" strokeWidth="1.5" />
        {/* Right hemisphere */}
        <path d="M260,120 Q340,80 380,160 Q420,240 400,320 Q380,400 300,420 L260,400Z"
          fill={highlightTarget === "right" ? accentColor : "#ef5350"} opacity={highlightTarget === "right" ? 1 : 0.7}
          stroke="#ef9a9a" strokeWidth="1.5" />
        {/* Cerebellum */}
        <ellipse cx="260" cy="440" rx="80" ry="35"
          fill={highlightTarget === "cerebellum" ? accentColor : "#c62828"} opacity="0.8"
          stroke="#ef9a9a" strokeWidth="1" />
        {/* Folds (sulci) */}
        {[
          "M180,180 Q220,170 240,200", "M160,260 Q200,250 220,280",
          "M300,180 Q330,170 350,200", "M310,260 Q340,250 360,280",
          "M200,340 Q240,330 260,350", "M280,340 Q310,330 340,350",
        ].map((d, i) => (
          <path key={i} d={d} fill="none" stroke="#ef9a9a" strokeWidth="1.2" opacity="0.5" />
        ))}
        {/* Central fissure */}
        <line x1="260" y1="110" x2="260" y2="400" stroke="#ffcdd2" strokeWidth="2" opacity="0.6" />
        {/* Stem */}
        <rect x="250" y="470" width="20" height="40" rx="5" fill="#b71c1c" opacity="0.7" />
      </g>
      {/* Labels */}
      <text x="300" y="240" textAnchor="middle" fill="#ecf0f1" fontSize="13">Left Hemisphere</text>
      <text x="540" y="240" textAnchor="middle" fill="#ecf0f1" fontSize="13">Right Hemisphere</text>
      <text x="400" y="510" textAnchor="middle" fill="#ecf0f1" fontSize="13">Cerebellum</text>
      <text x="400" y="570" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Eye Anatomy ──────────────────────────────────────── */
export const EyeAnatomy: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Human Eye";
  const highlightTarget = (props.highlightTarget as string) || "";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        {bodyGrad("eye-bg", "#1a2632", "#0d1117")}
        <radialGradient id="eye-iris" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#1565c0" /><stop offset="100%" stopColor="#0d47a1" />
        </radialGradient>
      </defs>
      <rect width="800" height="600" fill="url(#eye-bg)" rx="16" />
      <g transform="translate(160,60)">
        {/* Eyeball cross-section */}
        <ellipse cx="240" cy="240" rx="200" ry="190"
          fill={highlightTarget === "sclera" ? "#e3f2fd" : "#e8e8e8"} opacity="0.9"
          stroke="#bdbdbd" strokeWidth="2" />
        {/* Choroid */}
        <ellipse cx="240" cy="240" rx="185" ry="175"
          fill={highlightTarget === "choroid" ? "#c62828" : "#6d4c41"} opacity="0.6" />
        {/* Retina */}
        <ellipse cx="240" cy="240" rx="170" ry="160"
          fill={highlightTarget === "retina" ? "#ffab91" : "#ffccbc"} opacity="0.3" />
        {/* Vitreous humor */}
        <ellipse cx="260" cy="240" rx="140" ry="140" fill="#e3f2fd" opacity="0.2" />
        {/* Iris */}
        <circle cx="120" cy="240" r="50"
          fill={highlightTarget === "iris" ? "#42a5f5" : "url(#eye-iris)"} opacity="0.9" />
        {/* Pupil */}
        <circle cx="120" cy="240" r="22" fill="#0a0a0a" />
        {/* Cornea */}
        <path d="M70,200 Q60,240 70,280" fill="none"
          stroke={highlightTarget === "cornea" ? "#64b5f6" : "#90caf9"} strokeWidth="4" />
        {/* Lens */}
        <ellipse cx="150" cy="240" rx="20" ry="45"
          fill={highlightTarget === "lens" ? "#fff9c4" : "#fff176"} opacity="0.5"
          stroke="#fbc02d" strokeWidth="1.5" />
        {/* Optic nerve */}
        <path d="M440,230 L490,220 Q510,225 510,240 Q510,255 490,260 L440,250"
          fill={highlightTarget === "optic_nerve" ? "#fff176" : "#fbc02d"} opacity="0.7" />
      </g>
      <text x="400" y="565" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Ear Anatomy ──────────────────────────────────────── */
export const EarAnatomy: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Human Ear";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const parts = (props.parts as string[]) || ["Outer Ear", "Middle Ear", "Inner Ear"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>{bodyGrad("ear-bg", "#1a2632", "#0d1117")}</defs>
      <rect width="800" height="600" fill="url(#ear-bg)" rx="16" />
      {/* Outer ear */}
      <path d="M120,160 Q80,240 100,340 Q120,400 180,420 L200,380 Q160,340 160,280 Q160,200 200,160Z"
        fill={activeIndex === 0 ? "#e57373" : "#ef9a9a"} opacity={activeIndex === 0 ? 1 : 0.6}
        stroke="#ef9a9a" strokeWidth="1.5" />
      {/* Ear canal */}
      <rect x="200" y="260" width="120" height="40" rx="10"
        fill={activeIndex === 0 ? "#e57373" : "#ffcdd2"} opacity="0.5" />
      {/* Eardrum */}
      <ellipse cx="340" cy="280" rx="5" ry="30"
        fill={activeIndex === 1 ? "#fff176" : "#fbc02d"} stroke="#f57f17" strokeWidth="1.5" />
      {/* Middle ear - ossicles */}
      <g opacity={activeIndex === 1 ? 1 : 0.6}>
        <circle cx="380" cy="260" r="10" fill="#bdbdbd" stroke="#757575" strokeWidth="1.5" />
        <circle cx="410" cy="270" r="8" fill="#bdbdbd" stroke="#757575" strokeWidth="1.5" />
        <circle cx="440" cy="260" r="10" fill="#bdbdbd" stroke="#757575" strokeWidth="1.5" />
        <line x1="390" y1="260" x2="402" y2="268" stroke="#757575" strokeWidth="2" />
        <line x1="418" y1="268" x2="432" y2="262" stroke="#757575" strokeWidth="2" />
      </g>
      {/* Inner ear - cochlea */}
      <g opacity={activeIndex === 2 ? 1 : 0.6}>
        <path d="M480,260 Q520,220 540,260 Q560,300 520,320 Q500,330 500,300 Q500,280 520,280"
          fill="none" stroke={activeIndex === 2 ? "#64b5f6" : "#42a5f5"} strokeWidth="5" strokeLinecap="round" />
        {/* Semicircular canals */}
        <path d="M500,220 Q540,180 570,220" fill="none" stroke="#7e57c2" strokeWidth="4" strokeLinecap="round" />
        <path d="M510,200 Q560,170 580,210" fill="none" stroke="#7e57c2" strokeWidth="3" strokeLinecap="round" />
        <path d="M520,185 Q555,175 565,200" fill="none" stroke="#7e57c2" strokeWidth="3" strokeLinecap="round" />
      </g>
      {/* Labels */}
      {[
        { x: 150, y: 450, t: parts[0] || "Outer Ear" },
        { x: 400, y: 360, t: parts[1] || "Middle Ear" },
        { x: 550, y: 370, t: parts[2] || "Inner Ear" },
      ].map((l, i) => (
        <text key={i} x={l.x} y={l.y} textAnchor="middle" fill={i === activeIndex ? "#fff" : "#bdc3c7"} fontSize="14" fontWeight="500">{l.t}</text>
      ))}
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. Skeleton System ──────────────────────────────────── */
export const SkeletonSystem: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Skeleton";
  const highlightTarget = (props.highlightTarget as string) || "";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>{bodyGrad("sk-bg", "#1a1a2e", "#0d0d1a")}</defs>
      <rect width="800" height="600" fill="url(#sk-bg)" rx="16" />
      <g transform="translate(300,20)">
        {/* Skull */}
        <ellipse cx="100" cy="50" rx="40" ry="50"
          fill={highlightTarget === "skull" ? "#fff" : "#e0e0e0"} opacity="0.85" />
        <circle cx="86" cy="45" r="6" fill="#1a1a2e" />
        <circle cx="114" cy="45" r="6" fill="#1a1a2e" />
        <path d="M90,65 L110,65" stroke="#1a1a2e" strokeWidth="2" />
        {/* Spine */}
        {Array.from({ length: 12 }, (_, i) => (
          <rect key={i} x="92" y={100 + i * 18} width="16" height="14" rx="3"
            fill={highlightTarget === "spine" ? "#fff" : "#bdbdbd"} opacity="0.75" />
        ))}
        {/* Ribcage */}
        {[0, 1, 2, 3, 4].map((i) => (
          <g key={i}>
            <path d={`M92,${130 + i * 22} Q60,${140 + i * 22} 50,${155 + i * 22}`}
              fill="none" stroke={highlightTarget === "ribs" ? "#fff" : "#bdbdbd"} strokeWidth="3" opacity="0.6" />
            <path d={`M108,${130 + i * 22} Q140,${140 + i * 22} 150,${155 + i * 22}`}
              fill="none" stroke={highlightTarget === "ribs" ? "#fff" : "#bdbdbd"} strokeWidth="3" opacity="0.6" />
          </g>
        ))}
        {/* Pelvis */}
        <path d="M60,330 Q100,350 140,330 Q150,360 130,380 L70,380 Q50,360 60,330Z"
          fill={highlightTarget === "pelvis" ? "#fff" : "#bdbdbd"} opacity="0.7" />
        {/* Arms */}
        <line x1="80" y1="120" x2="30" y2="200" stroke={highlightTarget === "arms" ? "#fff" : "#bdbdbd"} strokeWidth="6" strokeLinecap="round" />
        <line x1="30" y1="200" x2="10" y2="300" stroke="#bdbdbd" strokeWidth="5" strokeLinecap="round" />
        <line x1="120" y1="120" x2="170" y2="200" stroke={highlightTarget === "arms" ? "#fff" : "#bdbdbd"} strokeWidth="6" strokeLinecap="round" />
        <line x1="170" y1="200" x2="190" y2="300" stroke="#bdbdbd" strokeWidth="5" strokeLinecap="round" />
        {/* Legs */}
        <line x1="80" y1="380" x2="60" y2="480" stroke={highlightTarget === "legs" ? "#fff" : "#bdbdbd"} strokeWidth="7" strokeLinecap="round" />
        <line x1="60" y1="480" x2="50" y2="540" stroke="#bdbdbd" strokeWidth="5" strokeLinecap="round" />
        <line x1="120" y1="380" x2="140" y2="480" stroke={highlightTarget === "legs" ? "#fff" : "#bdbdbd"} strokeWidth="7" strokeLinecap="round" />
        <line x1="140" y1="480" x2="150" y2="540" stroke="#bdbdbd" strokeWidth="5" strokeLinecap="round" />
      </g>
      <text x="400" y="585" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. Digestive Tract ──────────────────────────────────── */
export const DigestiveTract: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Digestive System";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const organs = (props.organs as string[]) || ["Mouth", "Esophagus", "Stomach", "Small Intestine", "Large Intestine"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>{bodyGrad("dg-bg", "#1a2632", "#0d1117")}</defs>
      <rect width="800" height="600" fill="url(#dg-bg)" rx="16" />
      <g transform="translate(240,30)">
        {/* Mouth */}
        <ellipse cx="160" cy="30" rx="30" ry="15"
          fill={activeIndex === 0 ? "#e74c3c" : "#ef9a9a"} opacity={activeIndex === 0 ? 1 : 0.6} />
        <text x="220" y="35" fill="#ecf0f1" fontSize="12">{organs[0]}</text>
        {/* Esophagus */}
        <rect x="152" y="45" width="16" height="80" rx="6"
          fill={activeIndex === 1 ? "#e74c3c" : "#ef5350"} opacity={activeIndex === 1 ? 1 : 0.6} />
        <text x="220" y="90" fill="#ecf0f1" fontSize="12">{organs[1]}</text>
        {/* Stomach */}
        <path d="M140,125 Q100,140 90,180 Q80,230 120,250 Q160,260 180,230 Q200,200 180,160 Q170,130 160,125Z"
          fill={activeIndex === 2 ? "#e74c3c" : "#c62828"} opacity={activeIndex === 2 ? 1 : 0.6} />
        <text x="230" y="190" fill="#ecf0f1" fontSize="12">{organs[2]}</text>
        {/* Small intestine */}
        <path d="M140,260 Q100,280 120,310 Q160,330 180,310 Q200,290 220,310
          Q240,340 200,360 Q160,370 140,350 Q120,340 140,320"
          fill="none" stroke={activeIndex === 3 ? "#ff7043" : "#ff8a65"} strokeWidth="10"
          strokeLinecap="round" opacity={activeIndex === 3 ? 1 : 0.6} />
        <text x="270" y="320" fill="#ecf0f1" fontSize="12">{organs[3]}</text>
        {/* Large intestine */}
        <path d="M60,380 L60,280 Q60,250 90,250 L230,250 Q260,250 260,280
          L260,380 Q260,420 230,420 L90,420 Q60,420 60,380Z"
          fill="none" stroke={activeIndex === 4 ? "#8d6e63" : "#a1887f"} strokeWidth="14"
          strokeLinecap="round" opacity={activeIndex === 4 ? 1 : 0.5} />
        <text x="290" y="400" fill="#ecf0f1" fontSize="12">{organs[4]}</text>
      </g>
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Muscle Fiber ─────────────────────────────────────── */
export const MuscleFiber: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Muscle Fiber";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const layers = (props.layers as string[]) || ["Epimysium", "Fascicle", "Muscle Fiber", "Myofibril"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>{bodyGrad("mf-bg", "#1a1a2e", "#0d0d1a")}</defs>
      <rect width="800" height="600" fill="url(#mf-bg)" rx="16" />
      {/* Concentric cylinders - muscle cross-section */}
      {[
        { rx: 240, ry: 180, color: "#c62828" },
        { rx: 190, ry: 140, color: "#e53935" },
        { rx: 140, ry: 100, color: "#ef5350" },
        { rx: 80, ry: 55, color: "#e57373" },
      ].map((c, i) => (
        <g key={i}>
          <ellipse cx="350" cy="280" rx={c.rx} ry={c.ry}
            fill={i === activeIndex ? "#fff" : c.color}
            opacity={i === activeIndex ? 0.9 : 0.6}
            stroke={i === activeIndex ? "#fff" : "#ef9a9a"} strokeWidth={i === activeIndex ? 3 : 1} />
          {/* Label line */}
          <line x1={350 + c.rx} y1="280" x2="620" y2={160 + i * 60} stroke="#546e7a" strokeWidth="1" strokeDasharray="4,3" />
          <text x="630" y={165 + i * 60} fill={i === activeIndex ? "#fff" : "#bdc3c7"} fontSize="14" fontWeight="500">
            {layers[i]}
          </text>
        </g>
      ))}
      {/* Striations in inner fiber */}
      {Array.from({ length: 8 }, (_, i) => (
        <line key={i} x1={310 + i * 10} y1="240" x2={310 + i * 10} y2="320"
          stroke="#ffcdd2" strokeWidth="1" opacity="0.3" />
      ))}
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Tooth Cross Section ──────────────────────────────── */
export const ToothCrossSection: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Tooth Anatomy";
  const highlightTarget = (props.highlightTarget as string) || "";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>{bodyGrad("tooth-bg", "#1a2632", "#0d1117")}</defs>
      <rect width="800" height="600" fill="url(#tooth-bg)" rx="16" />
      {/* Gum line */}
      <rect x="200" y="260" width="400" height="30" rx="8" fill="#e57373" opacity="0.5" />
      <g transform="translate(280,40)">
        {/* Crown */}
        <path d="M60,80 Q60,20 120,20 Q180,20 180,80 L180,230 L60,230Z"
          fill={highlightTarget === "enamel" ? "#fff" : "#eceff1"} stroke="#bdbdbd" strokeWidth="2" />
        {/* Dentin */}
        <path d="M80,100 Q80,50 120,50 Q160,50 160,100 L160,230 L80,230Z"
          fill={highlightTarget === "dentin" ? "#fff9c4" : "#fff176"} opacity="0.5" />
        {/* Pulp */}
        <path d="M100,130 Q100,90 120,90 Q140,90 140,130 L140,230 L100,230Z"
          fill={highlightTarget === "pulp" ? "#ef5350" : "#e57373"} opacity="0.6" />
        {/* Root */}
        <path d="M80,230 Q70,340 90,400 Q100,430 110,440 L130,440 Q140,430 150,400 Q170,340 160,230Z"
          fill={highlightTarget === "root" ? "#fff9c4" : "#fff176"} opacity="0.4"
          stroke="#bdbdbd" strokeWidth="1.5" />
        {/* Root canal */}
        <path d="M105,230 Q100,340 110,400 L130,400 Q140,340 135,230Z"
          fill={highlightTarget === "root_canal" ? "#ef5350" : "#e57373"} opacity="0.4" />
        {/* Nerve at bottom */}
        <circle cx="120" cy="430" r="6" fill="#e57373" />
      </g>
      {/* Labels */}
      {[
        { y: 100, t: "Enamel", id: "enamel" }, { y: 160, t: "Dentin", id: "dentin" },
        { y: 200, t: "Pulp", id: "pulp" }, { y: 360, t: "Root", id: "root" },
      ].map((l) => (
        <g key={l.id}>
          <line x1="510" y1={l.y} x2="570" y2={l.y} stroke="#546e7a" strokeWidth="1" strokeDasharray="3,2" />
          <text x="580" y={l.y + 5} fill={highlightTarget === l.id ? "#fff" : "#bdc3c7"} fontSize="14">{l.t}</text>
        </g>
      ))}
      <text x="400" y="550" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Skin Layers ──────────────────────────────────────── */
export const SkinLayers: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Skin Structure";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const layers = (props.layers as string[]) || ["Epidermis", "Dermis", "Hypodermis"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>{bodyGrad("skin-bg", "#1a1a2e", "#0d0d1a")}</defs>
      <rect width="800" height="600" fill="url(#skin-bg)" rx="16" />
      {/* Skin layers */}
      {[
        { y: 100, h: 80, color: "#ffcc80", lbl: layers[0] },
        { y: 180, h: 160, color: "#ef9a9a", lbl: layers[1] },
        { y: 340, h: 140, color: "#fff176", lbl: layers[2] },
      ].map((l, i) => (
        <g key={i}>
          <rect x="120" y={l.y} width="480" height={l.h}
            fill={i === activeIndex ? "#fff" : l.color}
            opacity={i === activeIndex ? 0.8 : 0.35}
            stroke={i === activeIndex ? "#fff" : "#546e7a"} strokeWidth={i === activeIndex ? 2 : 1} />
          <text x="620" y={l.y + l.h / 2 + 5} fill={i === activeIndex ? "#fff" : "#bdc3c7"} fontSize="16" fontWeight="500">
            {l.lbl}
          </text>
        </g>
      ))}
      {/* Hair follicle */}
      <line x1="250" y1="60" x2="260" y2="300" stroke="#6d4c41" strokeWidth="3" />
      <ellipse cx="260" cy="310" rx="12" ry="20" fill="#8d6e63" opacity="0.5" />
      {/* Sweat gland */}
      <path d="M400,300 Q410,340 380,350 Q360,340 370,300" fill="none" stroke="#64b5f6" strokeWidth="2" />
      <line x1="385" y1="180" x2="385" y2="100" stroke="#64b5f6" strokeWidth="1.5" strokeDasharray="4,3" />
      {/* Blood vessels */}
      <path d="M180,250 Q220,240 260,250 Q300,260 340,250" fill="none" stroke="#e53935" strokeWidth="2" opacity="0.5" />
      {/* Nerve ending */}
      <path d="M450,260 L460,280 L440,300 L460,320" fill="none" stroke="#fbc02d" strokeWidth="2" opacity="0.5" />
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="600">{label}</text>
    </svg>
  );
};
